import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { findObjectUseCase } from "../../usecases/object";
// If you truly don't have findObjectsUseCase, tell me and I'll adapt.

export default function RawPickerField({
  value, // selected product id (string) OR pointer {id}
  object = {}, // current form object (waste record)
  onChangeObject, // (nextObject) => void
  placeholder = "Search raw materials...",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(async () => {
      const keyword = (q || "").trim();
      if (keyword.length < 2) {
        setItems([]);
        return;
      }

      setLoading(true);
      try {
        const raw = await findObjectUseCase().execute("raw_materials", {
          search: keyword,
          // limit: 10,
          keys: ["id", "itemName", "sku", "cost", "price"],
        });

        const com = await findObjectUseCase().execute("components", {
          search: keyword,
          // limit: 10,
          keys: ["id", "itemName", "sku", "cost", "price", "category"],
        });

        const isRaw = Array.isArray(raw) ? raw : [];
        const isCom = Array.isArray(com) ? com : [];

        const rawWithCategory = isRaw.map((item) => ({
          ...item,
          category: "raw_materials",
          source: "raw_materials",
        }));

        const comWithCategory = isCom.map((item) => ({
          ...item,
          category: "components",
          source: "components",
        }));

        // make sure array
        setItems([...rawWithCategory, ...comWithCategory]);
        // setItems(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Product search error:", e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [q]);

  // react-select options
  const options = useMemo(() => {
    return (items || []).map((p) => ({
      value: p.id,
      label: `${p.itemName} (${p.sku})`,
      data: p,
    }));
  }, [items]);

  // selected option (from object/value)
  const selectedId =
    typeof value === "string" ? value : value?.id || object?.product?.id;

  const selectedOption = useMemo(() => {
    if (!selectedId) return null;

    // try to find from current items first
    const found = (items || []).find((p) => p.id === selectedId);
    if (found) {
      return {
        value: found.id,
        label: `${found.itemName} (${found.sku})`,
        data: found,
      };
    }

    // fallback label if not in current items
    if (object?.itemName && object?.sku) {
      return {
        value: selectedId,
        label: `${object.itemName} (${object.sku})`,
        data: null,
      };
    }
    return null;
  }, [selectedId, items, object?.itemName, object?.sku]);

  const handleSelect = (opt) => {
    if (!opt) {
      // clear selection
      onChangeObject({
        ...object,
        product: null,
        itemName: "",
        sku: "",
        cost: null,
        quantity: null,
        category: "",
      });
      return;
    }

    const p = opt.data;

    const next = {
      ...object,
      product: { id: p.id }, // pointer style
      itemName: p.itemName,
      sku: p.sku,
      cost: Number(p.cost ?? p.price ?? 0),
      quantity: object.quantity ?? 1,
      category: p.category,
    };

    onChangeObject(next);
  };

  // const handleQty = (e) => {
  //   const qty = Number(e.target.value || 0);
  //   onChangeObject({ ...object, quantity: qty });
  // };

  const handleQty = (e) => {
    const value = e.target.value;

    // allow empty string while typing
    onChangeObject({
      ...object,
      quantity: value === "" ? "" : Number(value),
    });
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: state.isFocused ? "2px solid #83c14a" : "2px solid #E5E7EB",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(131, 193, 74, 0.1)" : "none",
      padding: "4px 8px",
      minHeight: "48px",
      transition: "all 0.3s ease",
      "&:hover": {
        borderColor: "#83c14a",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9CA3AF",
      fontSize: "0.9375rem",
    }),
    input: (provided) => ({
      ...provided,
      fontSize: "0.9375rem",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#83c14a"
        : state.isFocused
          ? "#F0F9FF"
          : "white",
      color: state.isSelected ? "white" : "#374151",
      padding: "12px 16px",
      cursor: "pointer",
      fontSize: "0.9375rem",
      fontWeight: state.isSelected ? "600" : "500",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "10px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      border: "2px solid #E5E7EB",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "0.9375rem",
      color: "#111827",
      fontWeight: "500",
    }),
  };

  return (
    <div className="col-12">
      <div style={{ marginBottom: "12px" }}>
        <label
          className="form-label fw-bold d-flex align-items-center gap-2"
          style={{
            color: "#111827",
            fontSize: "0.9375rem",
            marginBottom: "8px",
          }}
        >
          <i className="bi bi-search" style={{ color: "#83c14a" }}></i>
          Search Raw Materials
        </label>
      </div>

      <Select
        value={selectedOption}
        options={options}
        isDisabled={disabled}
        isLoading={loading}
        isClearable
        placeholder={placeholder}
        styles={customStyles}
        onInputChange={(inputValue, meta) => {
          if (meta.action === "input-change") setQ(inputValue);
        }}
        onChange={handleSelect}
        noOptionsMessage={() =>
          q.trim().length < 2
            ? "Type at least 2 characters…"
            : "No raw materials found"
        }
      />

      {!!object?.product?.id && (
        <div
          style={{
            marginTop: "20px",
            background: "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #E5E7EB",
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                background: "linear-gradient(135deg, #83c14a 0%, #6fa83e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="bi bi-check-lg"
                style={{ color: "#fff", fontSize: "0.875rem" }}
              ></i>
            </div>
            <span
              style={{
                fontSize: "0.9375rem",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              Raw Material Selected
            </span>
          </div>

          <div className="row g-3">
            <div className="col-12">
              <label
                className="form-label fw-bold d-flex align-items-center gap-2"
                style={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  marginBottom: "8px",
                }}
              >
                <i className="bi bi-box" style={{ color: "#6B7280" }}></i>
                Item Name
              </label>
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #E5E7EB",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  fontSize: "0.9375rem",
                  color: "#111827",
                  fontWeight: "500",
                }}
              >
                {object.itemName || "—"}
              </div>
            </div>

            <div className="col-12">
              <label
                className="form-label fw-bold d-flex align-items-center gap-2"
                style={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  marginBottom: "8px",
                }}
              >
                <i className="bi bi-hash" style={{ color: "#6B7280" }}></i>
                Quantity
              </label>
              <input
                className="form-control"
                type="number"
                min="0"
                value={object.quantity === "" ? "" : (object.quantity ?? 1)}
                onChange={handleQty}
                style={{
                  borderRadius: "8px",
                  border: "2px solid #E5E7EB",
                  padding: "10px 14px",
                  fontSize: "0.9375rem",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#83c14a";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(131, 193, 74, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <div className="col-12">
              <label
                className="form-label fw-bold d-flex align-items-center gap-2"
                style={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  marginBottom: "8px",
                }}
              >
                <i
                  className="bi bi-chat-left-text"
                  style={{ color: "#6B7280" }}
                ></i>
                Description / Remarks
              </label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Enter reason for waste, notes, etc."
                required
                value={object.description || ""}
                onChange={(e) =>
                  onChangeObject({
                    ...object,
                    description: e.target.value,
                  })
                }
                style={{
                  borderRadius: "8px",
                  border: "2px solid #E5E7EB",
                  padding: "10px 14px",
                  fontSize: "0.9375rem",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  resize: "vertical",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#83c14a";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(131, 193, 74, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
